// ImplementFlow — the autonomous "implement an issue" run, surfaced as a live
// pipeline. Unlike RefineFlow this owns a DEDICATED session (intent=implement)
// rather than the shared per-project chat session: the server seeds it with the
// autonomous implement→MR prompt and drives it to completion with no user turns.
//
// The user's only possible interaction is the DoR halt: when the picked issue
// isn't ready to build, the server emits a `dorBlocked` frame and this surface
// asks whether to proceed anyway or jump to Refine. Everything else is watch-only.

import { useEffect, useRef, useState } from "react";

import type { LinkedMr } from "@loom/shared/dashboard";
import { forgeOfProjectId } from "@loom/shared/forge";

import { createSession } from "../projects/useProjects";
import { useWsSession } from "../chat/useWsSession";
import { IdleReconnectDialog } from "../chat/IdleReconnectDialog";
import { useIssueDetail } from "../dashboard/useIssueDetail";
import { useT, type Translator } from "../i18n/index.js";
import { useSessionBridgeEnsure } from "../lib/sessionBeacon";

import { ImplementPipeline } from "./ImplementPipeline";
import { useFieldCopy } from "./copy";
import {
  FocusFrame,
  GhostButton,
  Headline,
  Help,
  Kicker,
  NextButton,
  type FocusStatus,
} from "./ui";

interface ImplementFlowProps {
  readonly projectId: number;
  readonly iid: number;
  readonly issueTitle: string;
  readonly issueWebUrl: string | null;
  readonly onRestart: () => void;
  readonly onOpenIssue: (iid: number) => void;
  /** Switch to the Refine flow for the same issue (DoR-halt "refine first"). */
  readonly onRefineInstead: () => void;
  /**
   * Build a SUB-ISSUE of the halted epic — i.e. do what the epic screen's own
   * copy offers ("pick one to build it").
   *
   * Without it the child chips called `onOpenIssue`, which leaves Focus entirely
   * for the classic dashboard's issue editor: the one screen whose whole purpose
   * is to say "implement these instead" sent the user somewhere they could not
   * implement anything. Optional so a caller that cannot re-target the flow (no
   * such caller today) degrades to the old open-the-issue behaviour.
   */
  readonly onImplementChild?: (iid: number) => void;
  /**
   * Return to the ISSUE PICKER ("Which issue should Loom build?") rather than
   * the mode picker. The header's `onBack` uses this while a DoR/epic halt is
   * on screen — the halt's whole point is "pick a different issue instead",
   * so that is Back's primary recovery path there, not two Backs away behind
   * the mode picker. Optional so a caller with no picker to return to (no such
   * caller today) degrades to `onRestart`, the prior behaviour.
   */
  readonly onPickAnotherIssue?: () => void;
  /**
   * RESUME mode: when set, attach to this EXISTING implement session (a
   * walked-away run reopened from the sidebar) instead of creating a fresh one.
   * The server-side run kept driving while the tab was closed; this surface just
   * re-attaches its stream and re-triggers the bridge (POST /reconnect). The
   * issue identity comes from the persisted run via `GET /sessions/:id`.
   */
  readonly resumeSessionId?: string;
}

/**
 * Does GitLab say this merge request will CLOSE the issue the user just
 * picked?
 *
 * The detail bundle fills `mergeRequests` from
 * `GET /issues/:iid/related_merge_requests`, whose contract is "MRs that
 * mention this issue" — not "MRs that resolve it". Loom's own split→implement
 * seed makes that over-match structural: the implement prompt tells the agent
 * to read the `_Part of #N_` parent and to write `Closes #<iid>`, so the ONE
 * MR that closes a sub-issue also name-drops its epic parent and every sibling
 * it deferred work to. Live on project 1393, `related_merge_requests` returns
 * !916 for #1085, #1086 AND #1087, while `closed_by` returns it only for
 * #1086 — the one it actually closes.
 *
 * `closesIssue` is the server's own closing-pattern parse over the MR bodies
 * that bundle already carries (`mrClosesIssue`,
 * apps/server/src/dashboard/issue-detail.ts) — the same derivation GitLab's
 * `closed_by` performs, at zero extra round-trips. It is declared OPTIONAL on
 * `LinkedMrSchema` (which is `.strict()`, so "additive" only works if the
 * schema says so) and absent means UNKNOWN: this returns false and the guard
 * fails OPEN, the same direction every other degrade on this screen takes.
 * Missing a duplicate-build warning costs a wasted run; a false one blocks a
 * build that has nothing wrong with it, and on an epic it replaces the halt
 * screen whose child chips are the only way to reach the pieces.
 */
const closesThisIssue = (mr: LinkedMr): boolean => mr.closesIssue === true;

/**
 * The branch prefix every Loom coding run pushes onto
 * (`loom-coding/<sessionId>`, packages/coding-bridge/src/clone.ts). A recreated
 * session appends `-r2` / `-r3` to the same name (apps/server/src/http/
 * sessions.ts), so the PREFIX is the stable part, not the whole string.
 */
const LOOM_SESSION_BRANCH_PREFIX = "loom-coding/";

/**
 * Did LOOM open this merge request?
 *
 * `author` cannot answer that. `open_mr` creates the MR through proxy-fetch
 * with the SESSION USER's own OAuth token (`createMergeRequest(deps.proxyDeps,
 * { userId: session.userId, … })`, packages/mcp-server/src/tools/open-mr.ts),
 * so a Loom-built MR is authored by the very same human as a hand-written one —
 * an author check would match nothing and silently call every MR "someone
 * else's". The one Loom marker that actually reaches the browser is the source
 * branch.
 *
 * This decides COPY ONLY — the block, the MR link and the override are
 * identical on both sides of it. What it refuses to attribute to Loom, and
 * whether it should:
 *  - a colleague's hand-written MR whose description says `Closes #<iid>`
 *    (ordinary for any issue a human started): refused, which is the point —
 *    the warning stays, it just stops claiming Loom built the thing.
 *  - a Loom MR opened from a renamed branch, or from some future non-
 *    `loom-coding/` lane: mis-read as someone else's. Costs a neutral headline
 *    where an accurate one was available; nothing the user can do changes.
 *  - a body from a server predating `sourceBranch` on the wire: neutral
 *    headline. (The field is required on `LinkedMrSchema` today, so the
 *    `typeof` is belt for a rolling deploy and for test fixtures that omit it,
 *    not a live path.)
 *  - a human who pushed onto Loom's own session branch and opened the MR
 *    themselves: attributed to Loom, which is honest — Loom's run produced the
 *    branch that MR is built on.
 */
const isLoomAuthoredMr = (mr: LinkedMr): boolean =>
  typeof mr.sourceBranch === "string" &&
  mr.sourceBranch.startsWith(LOOM_SESSION_BRANCH_PREFIX);

const sandboxStatus = (
  t: Translator,
  phase: ReturnType<typeof useWsSession>["sandbox"]["phase"],
): FocusStatus => {
  switch (phase) {
    case "ready":
      return { tone: "live", label: t("focus.implement.sandboxLive") };
    case "degraded":
      return { tone: "bad", label: t("focus.implement.reconnecting") };
    case "terminated":
      return { tone: "bad", label: t("focus.implement.sandboxStopped") };
    case "idle":
      return { tone: "idle", label: t("focus.implement.starting") };
    default:
      return { tone: "work", label: t("focus.implement.booting") };
  }
};

/**
 * One epic-halt child chip. `dorBlocked.childIids` (the WS `implement_stage`
 * frame) carries bare issue numbers — no titles — so this fetches its own via
 * the same per-issue endpoint the classic issue page uses (`useIssueDetail`),
 * one round-trip per child, and falls back to the bare number while it's in
 * flight or on error. R2-U: the chip used to render `#1086` with the title
 * available only as a hover tooltip — the copy above it ("Pick one and Loom
 * builds it") has nothing to pick BY without it on screen.
 */
function EpicChildChip({
  projectId,
  childIid,
  onClick,
}: {
  readonly projectId: number;
  readonly childIid: number;
  readonly onClick: () => void;
}) {
  const t = useT();
  const { data } = useIssueDetail(projectId, childIid);
  const title = data?.issue.title ?? "";
  return (
    <button
      type="button"
      className="focus-chip is-open"
      onClick={onClick}
      title={t("focus.implement.epicHalt.buildChild", { iid: String(childIid) })}
    >
      {title
        ? t("focus.implement.titleWithName", { iid: childIid, title })
        : `#${childIid}`}
    </button>
  );
}

export function ImplementFlow({
  projectId,
  iid,
  issueTitle,
  issueWebUrl,
  onRestart,
  onOpenIssue,
  onRefineInstead,
  onImplementChild,
  onPickAnotherIssue,
  resumeSessionId,
}: ImplementFlowProps) {
  const t = useT();
  const fieldCopy = useFieldCopy();
  // GitHub opens a pull request and has no review app: the copy, the steps and
  // the done screen follow the forge.
  const forge = forgeOfProjectId(projectId);
  const [sessionId, setSessionId] = useState<string | null>(
    resumeSessionId ?? null,
  );
  const [createError, setCreateError] = useState<string | null>(null);
  const startedRef = useRef(false);

  // The picker has no per-issue "already has an open MR" signal (its list
  // comes from `useProjectIssues`, which doesn't carry MR linkage) — so this
  // is the last point before a second, disconnected sandbox session gets
  // dispatched for an issue Loom already built. `useIssueDetail` is the same
  // per-issue fetch `EpicChildChip` below already uses; it carries
  // `mergeRequests`, so a single round-trip here is enough to tell "already
  // built, MR open" apart from "never built" without a picker-wide fetch
  // fan-out. Skipped entirely on RESUME — reattaching to an existing run is
  // not a new build, so there is nothing to warn about.
  //
  // The bundle's MR list is GitLab's `related_merge_requests`, which includes
  // every MR that merely MENTIONS the issue — so `state === "opened"` alone
  // is not "Loom already built this". `closesThisIssue` is the narrowing
  // (see its own doc): live on manul/zku/planungstool, one MR (!916, closing
  // #1086) made the epic parent #1085 and the deferred sibling #1087 both
  // claim they were already built.
  const mrCheck = useIssueDetail(projectId, resumeSessionId === undefined ? iid : null);
  const openMr =
    mrCheck.data?.mergeRequests?.find(
      (m) => m.state === "opened" && closesThisIssue(m),
    ) ?? null;
  // Whose MR is it? Only the headline and the override label turn on this
  // (see `isLoomAuthoredMr`); the guard itself does not.
  const openMrIsLoom = openMr !== null && isLoomAuthoredMr(openMr);
  // A fetch error must not trap the user off this screen forever — it
  // degrades to "proceed", the same as "no MR found", never to a silent
  // block. That leaves the check able to miss a positive but never able to
  // strand a build that has nothing wrong with it.
  //
  // `mrCheck.error` alone cannot carry that promise, because a request can
  // fail to ANSWER rather than fail: `httpGet` passes no AbortSignal, so a
  // socket that never settles leaves `loading: true, error: null` for as long
  // as the tab lives — and this screen is built to sit in a tab for the length
  // of a whole autonomous build. `awaitingMrCheck` would latch on that state
  // and the create would never fire: no build, no error, just a spinner. The
  // timeout is the same "must not strand a build with nothing wrong with it"
  // degrade, for the one class of failure the hook cannot surface as an error.
  // (A 401 is NOT that class — `useIssueDetail` settles it with
  // `data: null, loading: false, error: null`, which reaches "proceed" here on
  // the very next render.)
  const [mrCheckTimedOut, setMrCheckTimedOut] = useState(false);
  useEffect(() => {
    if (resumeSessionId !== undefined || !mrCheck.loading) return;
    const timer = setTimeout(() => setMrCheckTimedOut(true), 10_000);
    return () => clearTimeout(timer);
  }, [resumeSessionId, mrCheck.loading]);
  const awaitingMrCheck =
    resumeSessionId === undefined &&
    mrCheck.loading &&
    mrCheck.error === null &&
    !mrCheckTimedOut;
  const [confirmedRebuild, setConfirmedRebuild] = useState(false);
  // Mirrors `startedRef` as STATE, because this one is read in render. The
  // guard's whole job is to stop a build BEFORE it starts; once a build is
  // dispatched the same screen would be a lie — it offers to start something
  // that is already running, and it hides the live progress list behind
  // itself. Reachable via the timeout above: the check gives up at 10s, the
  // build starts, and the slow fetch then answers "there is an open MR".
  const [buildStarted, setBuildStarted] = useState(false);
  const blockedByOpenMr =
    resumeSessionId === undefined &&
    openMr !== null &&
    !confirmedRebuild &&
    !buildStarted;

  // Open the dedicated implement session exactly once. The server triggers the
  // sandbox + runs the DoR gate; this surface just subscribes to the stream. In
  // RESUME mode the session already exists (it kept driving server-side while the
  // tab was closed), so skip the create and re-trigger its bridge below.
  useEffect(() => {
    if (startedRef.current) return;
    if (resumeSessionId !== undefined) {
      startedRef.current = true;
      setSessionId(resumeSessionId);
      return;
    }
    // Hold off dispatching a pipeline until the open-MR check has answered
    // and, if it found one, the user explicitly confirmed a rebuild.
    if (awaitingMrCheck || blockedByOpenMr) return;
    startedRef.current = true;
    setBuildStarted(true);
    void createSession(projectId, {
      intent: "implement",
      issueIid: iid,
      issueTitle,
      issueWebUrl,
    })
      .then((r) => setSessionId(r.sessionId))
      .catch((err) =>
        setCreateError(err instanceof Error ? err.message : String(err)),
      );
  }, [
    projectId,
    iid,
    issueTitle,
    issueWebUrl,
    resumeSessionId,
    awaitingMrCheck,
    blockedByOpenMr,
  ]);

  // RESUME mode: make sure a bridge pipeline is running for the reopened
  // session. The create path already triggers, so this only fires when
  // resuming — hence `?? null` rather than the live `sessionId`.
  useSessionBridgeEnsure(resumeSessionId ?? null);

  const ws = useWsSession(sessionId);
  const impl = ws.implement;

  const dorBlocked = impl?.dorBlocked ?? null;
  const mrUrl = impl?.mrUrl ?? null;
  const environments = impl?.environments ?? [];
  // Done only when the whole pipeline (incl. the review deploy) has settled —
  // the MR opening (mrUrl set) is mid-pipeline, not the end.
  const isDone = impl?.stage === "done";
  const isFailed =
    !dorBlocked &&
    !isDone &&
    (impl?.status === "failed" || ws.sandbox.phase === "terminated");
  const inDeploy = !isDone && !isFailed && impl?.stage === "deploy";

  const title = issueTitle
    ? t("focus.implement.titleWithName", { iid, title: issueTitle })
    : t("focus.implement.titleFallback", { iid });

  const mrLink = mrUrl ? (
    <a className="focus-btn" href={mrUrl} target="_blank" rel="noreferrer">
      {t(forge === "github" ? "focus.implement.viewPr" : "focus.implement.viewMr")}
    </a>
  ) : null;
  const envLinks =
    environments.length > 0 ? (
      <div className="impl-envs">
        {environments.map((e) => (
          <a
            key={e.url}
            className="focus-btn is-ok"
            href={e.url}
            target="_blank"
            rel="noreferrer"
          >
            {t("focus.implement.openEnv", { name: e.name })}
          </a>
        ))}
      </div>
    ) : null;

  return (
    <>
      {(ws.idleCountdownEndsAt !== null || ws.status === "idle_killed") && (
        <IdleReconnectDialog
          endsAt={ws.idleCountdownEndsAt}
          killed={ws.status === "idle_killed"}
          onStay={ws.dismissIdle}
        />
      )}
      <FocusFrame
      stepKey="implement"
      progress={isDone ? 1 : 0.5}
      // A DoR/epic halt's recovery path is "pick a different issue" — send
      // Back straight to the issue picker there instead of the mode picker
      // two screens further out. Live, reproduced twice: from the epic-halt
      // screen, Back landed on "What would you like to do?", skipping "Which
      // issue should Loom build?" entirely.
      onBack={
        dorBlocked !== null || blockedByOpenMr
          ? (onPickAnotherIssue ?? onRestart)
          : onRestart
      }
      status={sandboxStatus(t, ws.sandbox.phase)}
    >
      <Kicker>{t("focus.implement.kicker")}</Kicker>

      {createError ? (
        <>
          <Headline>{t("focus.implement.startError.headline")}</Headline>
          <div className="focus-error">{createError}</div>
          <NextButton onClick={onRestart} label={t("common.back")} showEnterHint={false} />
        </>
      ) : blockedByOpenMr && openMr ? (
        <>
          <Headline>
            {t(
              openMrIsLoom
                ? "focus.implement.openMr.headline"
                : "focus.implement.openMr.headlineOther",
            )}
          </Headline>
          <Help>
            {t("focus.implement.openMr.help", { title, mrTitle: openMr.title })}
          </Help>
          <div className="focus-footer">
            <a
              className="focus-btn"
              href={openMr.webUrl}
              target="_blank"
              rel="noreferrer"
            >
              {t("focus.implement.viewMr")}
            </a>
            <GhostButton onClick={() => setConfirmedRebuild(true)}>
              {t(
                openMrIsLoom
                  ? "focus.implement.openMr.buildAnyway"
                  : "focus.implement.openMr.buildAnywayOther",
              )}
            </GhostButton>
          </div>
        </>
      ) : dorBlocked ? (
        <>
          <Headline>{title}</Headline>
          <Help>
            {dorBlocked.haltKind === "epic"
              ? (dorBlocked.childIids ?? []).length > 0
                ? t("focus.implement.epicHalt.help")
                : t("focus.implement.epicHalt.helpNoChildren")
              : t("focus.implement.dor.help", {
                  // `missing` carries FIELD IDS (role / ends /
                  // acceptance_criteria). The same fields are called "User",
                  // "Outcome" and "Acceptance criteria" everywhere else in this
                  // UI, so the ids are shown through the same label map.
                  missing:
                    dorBlocked.missing.length > 0
                      ? dorBlocked.missing
                          .map((f) => fieldCopy(f).label)
                          .join(", ")
                      : t("focus.implement.dor.missingFallback"),
                })}
          </Help>
          {dorBlocked.haltKind === "epic" &&
            (dorBlocked.childIids ?? []).length > 0 && (
              <div className="focus-epic-children">
                {(dorBlocked.childIids ?? []).map((c) => (
                  <EpicChildChip
                    key={c}
                    projectId={projectId}
                    childIid={c}
                    // Builds the child — the action the copy above promises.
                    onClick={() =>
                      onImplementChild ? onImplementChild(c) : onOpenIssue(c)
                    }
                  />
                ))}
              </div>
            )}
          <div className="focus-footer">
            {/* "Refine first" is the DoR-gate remedy: the issue is missing
                readiness fields, so refining fills them. An EPIC halt is not
                that — live it offered "Refine first" for an issue already
                labelled `loom::dor:pass`, i.e. a remedy for a problem the issue
                did not have. An epic WITH children is answered by picking one;
                an epic with none is answered by splitting it, which is what
                refine does, so that case keeps a button and names the split. */}
            {dorBlocked.haltKind === "epic" ? (
              (dorBlocked.childIids ?? []).length === 0 && (
                <button
                  type="button"
                  className="focus-btn"
                  onClick={onRefineInstead}
                >
                  {t("focus.implement.epicHalt.splitFirst")}
                </button>
              )
            ) : (
              <button
                type="button"
                className="focus-btn"
                onClick={onRefineInstead}
              >
                {t("focus.implement.dor.refineFirst")}
              </button>
            )}
            <GhostButton onClick={() => ws.sendImplementProceed()}>
              {dorBlocked.haltKind === "epic"
                ? t("focus.implement.epicHalt.proceedAnyway")
                : t("focus.implement.dor.proceedAnyway")}
            </GhostButton>
          </div>
        </>
      ) : isDone ? (
        <>
          <Headline>
            {environments.length > 0
              ? t("focus.implement.done.reviewLive")
              : t("focus.implement.done.allDone")}
          </Headline>
          <Help>
            {environments.length > 0
              ? t("focus.implement.done.helpWithReview", { title })
              : forge === "github"
                ? t("focus.implement.done.helpPr", { title })
                : t("focus.implement.done.help", {
                    title,
                    detail: impl?.detail ? ` ${impl.detail}.` : "",
                  })}
          </Help>
          <ImplementPipeline phase={ws.sandbox.phase} implement={impl} forge={forge} />
          {envLinks}
          <div className="focus-footer">
            {mrLink}
            <GhostButton onClick={() => onOpenIssue(iid)}>
              {t("focus.implement.openIssue", { iid })}
            </GhostButton>
          </div>
        </>
      ) : isFailed ? (
        <>
          <Headline>{t("focus.implement.failed.headline")}</Headline>
          <Help>
            {impl?.detail ??
              t(
                forge === "github"
                  ? "focus.implement.failed.defaultDetailPr"
                  : "focus.implement.failed.defaultDetail",
              )}{" "}
            {t("focus.implement.failed.help")}
          </Help>
          <ImplementPipeline phase={ws.sandbox.phase} implement={impl} forge={forge} />
          <div className="focus-footer">
            {mrLink}
            <button type="button" className="focus-btn" onClick={onRestart}>
              {t("common.back")}
            </button>
            <GhostButton onClick={() => onOpenIssue(iid)}>
              {t("focus.implement.openIssue", { iid })}
            </GhostButton>
          </div>
        </>
      ) : (
        <>
          <Headline>{title}</Headline>
          <Help>
            {inDeploy
              ? t("focus.implement.deploy.help")
              : t(
                  forge === "github"
                    ? "focus.implement.build.helpPr"
                    : "focus.implement.build.help",
                )}
          </Help>
          <ImplementPipeline phase={ws.sandbox.phase} implement={impl} forge={forge} />
          {inDeploy && mrUrl && <div className="focus-footer">{mrLink}</div>}
        </>
      )}
    </FocusFrame>
    </>
  );
}
